import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/auth-context';
import { useApi } from '@/hooks/useApi';
import { approvalClient } from '@/lib/approval-client';
import type {
  ApprovalBox,
  ApprovalLine,
  ApprovalProgram,
  ApprovalRequestSummary,
  ApprovalStatus,
  ApprovalView,
  DealerPriceCreateRequest,
  LineResult,
  StorePriceCreateRequest,
} from '@/types/approval';
import dayjs from 'dayjs';

/** 처리상태 라벨 - 취소 라벨은 PRD 표준(취소미승인/취소승인)을 따른다 (설계서 §7.5 v2.4) */
const STATUS_LABEL: Record<
  ApprovalStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className?: string }
> = {
  SUBMITTED: { label: '미승인', variant: 'secondary' },
  IN_PROGRESS: { label: '결재중', variant: 'secondary', className: 'border border-primary/40 text-primary' },
  APPROVED: { label: '승인', variant: 'default' },
  APPLIED_PENDING: { label: '적용대기', variant: 'default' },
  APPLIED: { label: '적용완료', variant: 'default' },
  CANCEL_REQUESTED: { label: '취소미승인', variant: 'outline', className: 'border-destructive/40 text-destructive' },
  CANCELLED: { label: '취소승인', variant: 'outline', className: 'border-destructive/40 text-destructive' },
  REJECTED: { label: '반려', variant: 'destructive' },
};

const RESULT_LABEL: Record<LineResult, string> = {
  PENDING: '대기',
  APPROVED: '승인',
  REJECTED: '반려',
  SKIPPED: '자동통과',
  VOID: '결재불요',
};

const HISTORY_LABEL: Record<string, string> = {
  SUBMIT: '상신',
  SKIP: '자동통과',
  APPROVE: '승인',
  REJECT: '반려',
  CANCEL: '자진취소',
  CANCEL_REQUEST: '취소요청',
  CANCEL_APPROVE: '취소승인',
  CANCEL_REJECT: '취소반려',
  APPLY: '적용완료',
  REMIND: '리마인더',
};

export const PROGRAM_TITLE: Record<ApprovalProgram, string> = {
  'store-price': '주유소 판매단가',
  'dealer-price': '거래처 계약단가',
};

/** 조회구분 라디오 5종. 라벨이 표준이라 바꾸지 않는다 (설계서 v2.4, PRD 4종 공통) */
export const VIEW_OPTIONS: { value: ApprovalView; label: string }[] = [
  { value: 'UNAPPROVED', label: '미승인' },
  { value: 'APPROVED', label: '승인' },
  { value: 'CANCEL_UNAPPROVED', label: '취소미승인' },
  { value: 'CANCEL_APPROVED', label: '취소승인' },
  { value: 'ALL', label: '전체(반려포함)' },
];

/** 적용구분 (V62 request_price_detail.apply_type 주석). 4·B 만 POS 마감 시 적용된다 */
export const APPLY_TYPES: { code: string; name: string }[] = [
  { code: '1', name: '생산가' },
  { code: '2', name: '공장도가' },
  { code: '3', name: '대리점일반유' },
  { code: '4', name: '주유소일반유' },
  { code: '5', name: '대리점면세유' },
  { code: '6', name: '주유소면세유' },
  { code: '7', name: '주유소특소세유' },
  { code: '8', name: '현물단가' },
  { code: '9', name: '고시가' },
  { code: 'A', name: 'SELF단가' },
  { code: 'B', name: '현금DC' },
];

/** 판매구분 (geo_dg.oil_sale_type) */
export const OIL_SALE_TYPES: { code: string; name: string }[] = [
  { code: '1', name: 'Dis' },
  { code: '2', name: 'Bulk' },
  { code: '3', name: '용기자차' },
  { code: '4', name: '용기거래처차' },
  { code: '5', name: '용차' },
  { code: '6', name: '거래처자차' },
  { code: '7', name: '자차' },
];

/** 단가적용방법 (geo_dg.price_app_method) */
export const PRICE_APP_METHODS: { code: string; name: string }[] = [
  { code: '1', name: 'DC율' },
  { code: '2', name: 'DC단가' },
  { code: '3', name: '확정단가' },
];

export function StatusBadge({ status }: { status: ApprovalStatus }) {
  const meta = STATUS_LABEL[status] ?? { label: status, variant: 'secondary' as const };
  return (
    <Badge variant={meta.variant} className={meta.className}>
      {meta.label}
    </Badge>
  );
}

export function statusLabel(status: string): string {
  return STATUS_LABEL[status as ApprovalStatus]?.label ?? status;
}

export function resultLabel(result: LineResult): string {
  return RESULT_LABEL[result] ?? result;
}

export function historyLabel(action: string): string {
  return HISTORY_LABEL[action] ?? action;
}

export function formatDateTime(value: string | null): string {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';
}

export function formatNumber(value: unknown): string {
  // Number(null)·Number('')은 0이라 미입력이 '0원'으로 보인다 - 먼저 걸러낸다
  if (value === null || value === undefined || value === '') return '-';
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '-';
}

/** requestData(JSONB)에서 화면 표시용 값 뽑기 - 프로그램별 키는 api 어댑터가 채운다 */
export function requestDataValue(data: Record<string, unknown> | null, key: string): string {
  const value = data?.[key];
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}

/** 목록 한 줄에 쓰는 품목 표기 - 거래처가 있으면 앞에 붙인다 */
export function requestItemLabel(data: Record<string, unknown> | null): string {
  const d = data ?? {};
  return [d.dealerName, d.productName || d.productCode].filter(Boolean).join(' / ') || '-';
}

const APPROVAL_CHANGED = 'approval-changed';

/** 문서를 처리하면 목록과 레일 배지가 같이 갱신돼야 한다 (대시보드 'dashboard-refresh' 와 같은 방식) */
export function notifyApprovalChanged() {
  window.dispatchEvent(new Event(APPROVAL_CHANGED));
}

/** SPA 내부 이동. 새 창이라 경로는 항상 절대로 만든다 (orgCode 프리픽스 유지) */
export function useApprovalNav() {
  const { orgCode } = useParams();
  const navigate = useNavigate();
  const root = `/${orgCode}/approvals`;
  return {
    root,
    /** 메인 앱 홈. 결재는 새 창이라 SPA 밖으로 나가려면 전체 경로가 필요하다 */
    appHome: `/${orgCode}`,
    goList: (box: ApprovalBox, program: ApprovalProgram) => navigate(`${root}/${box}/${program}`),
    goHome: () => navigate(root),
  };
}

/** 로그인 사용자의 소속·직책. 조회조건이 사업장·부서 입력을 고정할지 판단한다 (B210 §4.1, A330 §4.1) */
export function useMyProfile() {
  const { data } = useApi(() => approvalClient.profile(), []);
  return data;
}

/**
 * 레일 배지용 프로그램별 내 처리 대기 건수. 결재함의 미승인·취소미승인은 지금 내 차례인 건만 세므로(§9.4)
 * 둘을 더한 값이 내 처리 대기다 - 취소결재를 빼면 취소요청 건이 배지에서 사라진다.
 * api 요약(/me/approvals/summary)은 프로그램 무관 합계라 못 쓴다
 */
export function useInboxPendingCounts(programs: ApprovalProgram[]) {
  const key = programs.join(',');
  const { data, refetch } = useApi<Record<string, number>>(async () => {
    const counts = await Promise.all(programs.map(program => approvalClient.counts('inbox', program)));
    return Object.fromEntries(programs.map((program, i) => [program, counts[i].unapproved + counts[i].cancelUnapproved]));
  }, [key]);
  useEffect(() => {
    window.addEventListener(APPROVAL_CHANGED, refetch);
    return () => window.removeEventListener(APPROVAL_CHANGED, refetch);
  }, [refetch]);
  return data ?? {};
}

/** 상신 본문이 완성되면 500ms 뒤 결재선을 조회한다. 입력마다 부르지 않으려는 지연이다 */
export function useLinePreview(program: ApprovalProgram, body: StorePriceCreateRequest | DealerPriceCreateRequest | null) {
  const key = body ? JSON.stringify(body) : '';
  const [settled, setSettled] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSettled(key), 500);
    return () => clearTimeout(t);
  }, [key]);
  return useApi<ApprovalLine[] | null>(
    () => (settled ? approvalClient.previewLine(program, JSON.parse(settled)) : Promise.resolve(null)),
    [program, settled]
  );
}

/**
 * 결재할 사람이 한 명도 없는 결재선인지. 전 단계가 자동통과면 결재 없이 최종승인으로 직행하므로 상신을 막는다.
 * 프로그램 미사용(빈 결재선)은 전자결재를 안 쓰겠다는 설정이라 해당하지 않는다.
 */
export function hasNoRealApprover(lines: ApprovalLine[] | null | undefined): boolean {
  return !!lines?.length && !lines.some(line => line.result === 'PENDING');
}

/** 등록 다이얼로그의 결재선 미리보기. 누가 결재할지 모른 채 상신하지 않도록 상세와 같은 도장열로 보여준다 */
export function LinePreview({
  body,
  preview,
}: {
  /** 필수값이 비면 null - 조회하지 않는다 */
  body: StorePriceCreateRequest | DealerPriceCreateRequest | null;
  preview: ReturnType<typeof useLinePreview>;
}) {
  const { user } = useAuth();
  const { data, loading, error } = preview;
  let content: React.ReactNode;
  if (!body) {
    content = <p className='text-xs text-muted-foreground'>필수 항목을 모두 입력하면 결재선이 표시됩니다</p>;
  } else if (error) {
    content = <p className='text-xs text-destructive'>{error.message}</p>;
  } else if (loading || !data) {
    content = <Skeleton className='h-[140px] w-72' />;
  } else if (data.length === 0) {
    content = <p className='text-xs text-muted-foreground'>전자결재를 사용하지 않는 프로그램이라 결재 없이 바로 승인됩니다</p>;
  } else {
    content = (
      <div className='space-y-2'>
        <ApprovalStampRow request={{ status: 'SUBMITTED', drafterName: user?.username ?? null, createdAt: null }} lines={data} />
        {hasNoRealApprover(data) && (
          <p className='text-xs text-destructive'>
            결재선에 결재할 사람이 없습니다. 이대로는 상신할 수 없으니 전결규정을 확인해 주세요
          </p>
        )}
      </div>
    );
  }
  return (
    <div className='space-y-1'>
      <Label className='block'>결재선</Label>
      {content}
    </div>
  );
}

/** 결재란 도장 표현 - 결과별로 테두리/라벨이 달라야 대기와 자동통과가 구분된다 */
const STAMP_STYLE: Record<LineResult, { box: string; name: string; label: string; cell?: string }> = {
  PENDING: { box: 'border-dashed border-muted-foreground/40', name: 'font-normal text-muted-foreground', label: '대기' },
  APPROVED: { box: 'border-primary text-primary', name: 'font-semibold', label: '승인' },
  REJECTED: { box: 'border-destructive text-destructive', name: 'font-semibold', label: '반려' },
  SKIPPED: {
    box: 'border-muted-foreground/30 text-muted-foreground',
    name: 'font-normal',
    label: '자동통과',
    cell: 'bg-muted/40',
  },
  // 이름에 취소선을 그으면 '배제된 사람'으로 읽힌다. 칸 전체를 죽이고 라벨로 상태를 말한다
  VOID: {
    box: 'border-muted-foreground/20 text-muted-foreground',
    name: 'font-normal',
    label: '결재불요',
    cell: 'bg-muted/40',
  },
};

/** 결재 진행 중인 문서에서만 현재 칸을 강조한다 (종결 문서에 '진행중' 링이 남으면 오히려 오독) */
const ACTIVE_STATUS: ApprovalStatus[] = ['SUBMITTED', 'IN_PROGRESS', 'CANCEL_REQUESTED'];

/**
 * 종이 결재란 형태의 결재선. 기안 칸은 lines 에 없으므로 request 에서 만들어 붙인다.
 * lines 는 서버가 보낸 순서(1차→N차→취소결재) 그대로 쓴다 - stepOrder 로 정렬하면 취소(0)가 앞으로 튄다.
 */
export function ApprovalStampRow({
  request,
  lines,
  selectedStep,
  onSelect,
}: {
  /** 미리보기는 요청이 아직 없어 필요한 세 값만 받는다 */
  request: Pick<ApprovalRequestSummary, 'status' | 'drafterName'> & { createdAt: string | null };
  lines: ApprovalLine[];
  /** 결재선 탭으로 쓸 때 선택된 단계 (PRD 결재 처리 카드). 생략하면 선택 없는 표시 전용이다 */
  selectedStep?: number | null;
  onSelect?: (stepOrder: number) => void;
}) {
  const activeStep = ACTIVE_STATUS.includes(request.status) ? lines.find(l => l.result === 'PENDING')?.stepOrder : undefined;

  return (
    <div
      className='inline-flex max-w-full overflow-x-auto rounded-md border bg-card'
      role='group'
      aria-label='결재선'
      tabIndex={0}
    >
      <div className='sticky left-0 z-10 flex w-8 shrink-0 items-center justify-center border-r bg-muted/60 text-xs tracking-[0.3em] text-muted-foreground [writing-mode:vertical-rl]'>
        결재
      </div>
      <StampCell title='기안' name={request.drafterName ?? '-'} at={request.createdAt} result='APPROVED' label='상신' />
      {lines.map(line => (
        <StampCell
          key={line.stepOrder}
          title={line.stepLabel ?? (line.stepOrder === 0 ? '취소결재' : `${line.stepOrder}차`)}
          name={line.approverName ?? `#${line.approverId}`}
          at={line.processedAt}
          result={line.result}
          delegated={line.isDelegated}
          active={line.stepOrder === activeStep}
          selected={selectedStep === line.stepOrder}
          onSelect={onSelect && (() => onSelect(line.stepOrder))}
        />
      ))}
    </div>
  );
}

function StampCell({
  title,
  name,
  at,
  result,
  delegated,
  active,
  label,
  selected,
  onSelect,
}: {
  title: string;
  name: string;
  at: string | null;
  result: LineResult;
  delegated?: boolean;
  active?: boolean;
  label?: string;
  selected?: boolean;
  onSelect?: () => void;
}) {
  const style = STAMP_STYLE[result];
  const Cell = onSelect ? 'button' : 'div';
  return (
    <Cell
      type={onSelect ? 'button' : undefined}
      onClick={onSelect}
      aria-pressed={onSelect ? !!selected : undefined}
      className={`w-32 shrink-0 border-r text-left last:border-r-0 ${style.cell ?? ''} ${active ? 'bg-primary/5' : ''} ${
        onSelect ? 'cursor-pointer hover:bg-accent/60' : ''
      } ${selected ? 'ring-2 ring-inset ring-foreground/40' : ''}`}
      aria-current={active ? 'step' : undefined}
    >
      <div className='truncate border-b bg-muted/60 px-2 py-1.5 text-center text-xs text-muted-foreground' title={title}>
        {title}
      </div>
      <div className='flex h-[108px] flex-col items-center justify-center gap-1 px-1'>
        <div
          className={`flex size-16 items-center justify-center rounded-full border-2 ${style.box} ${active ? 'ring-2 ring-primary/40 ring-offset-1' : ''}`}
          title={name}
        >
          <span className={`line-clamp-2 break-keep px-1 text-center text-[11px] leading-tight ${style.name}`}>{name}</span>
        </div>
        <span className='text-xs'>
          {label ?? style.label}
          {delegated && <span className='ml-0.5 text-[10px] text-muted-foreground'>(대리)</span>}
          {/* 진행 중 표시가 링(색)에만 실리면 흑백 인쇄·색각 이상에서 사라진다 */}
          {active && <span className='ml-0.5 text-[10px] text-primary'>(진행중)</span>}
        </span>
        <span className='h-3 text-[10px] text-muted-foreground'>{at ? dayjs(at).format('YY-MM-DD HH:mm') : ''}</span>
      </div>
    </Cell>
  );
}

/**
 * 거래처 계약단가 한 값 - 단가적용방법을 붙여야 읽힌다(DC율 −2% / DC단가 −15원 / 확정단가 1,550원).
 * 방법마다 단위가 달라 현재·요청의 증감은 계산하지 않는다(B220/A340 설계에 변동액 없음).
 */
export function contractPriceLabel(price: unknown, method: unknown, methodName?: unknown): string {
  if (price === null || price === undefined || price === '') return '-';
  const unit = method === '1' ? '%' : '원';
  const name = typeof methodName === 'string' && methodName ? `${methodName} ` : '';
  return `${name}${formatNumber(price)}${unit}`;
}

/** 거래처 계약단가 현재 → 요청 (증감 없이 값만) */
export function ContractPriceChange({ data }: { data: Record<string, unknown> | null }) {
  const current = contractPriceLabel(data?.currentPrice, data?.currentPriceAppMethod, data?.currentPriceAppMethodName);
  const requested = contractPriceLabel(data?.requestedPrice, data?.priceAppMethod, data?.priceAppMethodName);
  return (
    <span className='tabular-nums'>
      {current === '-' ? '신규' : current} → <span className='font-semibold'>{requested}</span>
    </span>
  );
}

/** 단가 변동 표시 - 숫자만 찍으면 인상인지 인하인지 알 수 없다. 판매단가 전용(거래처 계약단가는 ContractPriceChange) */
export function PriceDelta({
  current,
  requested,
  deltaOnly,
}: {
  current: unknown;
  requested: unknown;
  /** 현재가→요청가를 이미 크게 보여주는 자리에서는 증감분만 쓴다 */
  deltaOnly?: boolean;
}) {
  const from = Number(current);
  const to = Number(requested);
  if (!Number.isFinite(from) || !Number.isFinite(to) || from === 0) {
    return <span className='tabular-nums'>{formatNumber(requested)}</span>;
  }
  const diff = to - from;
  const rate = ((diff / from) * 100).toFixed(1);
  const tone = diff > 0 ? 'text-destructive' : diff < 0 ? 'text-primary' : 'text-muted-foreground';
  const delta = (
    <span className={`${deltaOnly ? 'font-medium' : 'ml-1 text-xs'} ${tone}`}>
      {diff > 0 ? '▲' : diff < 0 ? '▼' : ''}
      {formatNumber(Math.abs(diff))} ({rate}%)
    </span>
  );
  if (deltaOnly) return <span className='tabular-nums'>{delta}</span>;
  return (
    <span className='tabular-nums'>
      {formatNumber(current)} → <span className='font-semibold'>{formatNumber(requested)}</span>
      {delta}
    </span>
  );
}

/** 화면 안의 구역 박스. title 이 있으면 조용한 소제목을 얹는다 (어두운 카드 헤더 바를 쓰지 않는 이유) */
export function ApprovalSection({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <section className='rounded-lg border bg-card text-card-foreground'>
      {title && <h2 className='border-b px-4 py-2 text-xs font-medium text-muted-foreground'>{title}</h2>}
      {children}
    </section>
  );
}
